import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { IMAGE_SIZES } from '@shared/contants'

/*
  Resolves catalog category IDs to the slug (and icon) their links need.

  Why one query with aliases instead of `categories(ids:)`: the bulk query only
  returns TOP-LEVEL categories — its resolver adds `whereNull('parent_id')` when
  no parent is given — while these shortcuts normally point at nested ones. The
  singular `category(id:)` query has no such restriction, so the ids are aliased
  into a single request and resolved together.
*/
/*
  Built for a non-empty id list only: an empty selection set is invalid GraphQL,
  and declaring $style without using it is rejected by the server too.
*/
const buildCategoriesByIdQuery = (ids: string[]) => gql`
  query LandingCategoriesByIds($shopId: ID!, $style: String) {
    ${ids
    .map((id, index) => `
      c${index}: category(shop_id: $shopId, id: ${JSON.stringify(id)}) {
        id
        name
        slug
        icon(style: $style) { url }
      }
    `)
    .join('\n')}
  }
`

// Placeholder document for the skipped case — useQuery still needs a valid one.
const EMPTY_QUERY = buildCategoriesByIdQuery([ '0' ])

export interface ResolvedCategory {
  id: string;
  name?: string | null;
  slug?: string | null;
  iconUrl?: string | null;
}

type AliasedCategory = {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  icon?: { url?: string | null } | null;
} | null

export function useCategorySlugs (ids: string[], shopId: string) {
  // Stable key so the document (and Apollo's cache entry) is reused across
  // renders instead of being rebuilt every time.
  const key = ids.join(',')

  const query = useMemo(
    () => (ids.length > 0 ? buildCategoriesByIdQuery(ids) : EMPTY_QUERY),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands in for `ids`
    [ key ],
  )

  const { data, loading } = useQuery<Record<string, AliasedCategory>>(
    query,
    {
      variables: { shopId, style: IMAGE_SIZES.TABLET },
      skip: !shopId || ids.length === 0,
      fetchPolicy: 'cache-first',
      // A missing category answers with an error for that alias only — keep the
      // rest of the batch usable.
      errorPolicy: 'all',
    },
  )

  const categoriesById = useMemo(() => {
    const map = new Map<string, ResolvedCategory>()

    ids.forEach((id, index) => {
      const node = data?.[`c${index}`]

      if (node?.slug) {
        map.set(id, {
          id,
          name: node.name,
          slug: node.slug,
          iconUrl: node.icon?.url ?? null,
        })
      }
    })

    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands in for `ids`
  }, [ data, key ])

  return { categoriesById, loading }
}
